// src/components/calendar/CalendarTable.js
import React, { useContext, useMemo, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, Tooltip, CircularProgress
} from "@mui/material";
import CalendarCell from "./CalendarCell";
import StaffingQuickEdit from "./StaffingQuickEdit";
import { UserContext } from "../context/UserContext";
import { hasAnyRequiredPermission, STAFFING_WRITE_PERMISSIONS } from "../../config/routePermissions";
import { format, parseISO, getDay } from "date-fns";
import { es } from "date-fns/locale";
import { useTheme } from "@mui/material/styles";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";

const getShiftName = (employee) => {
  const ws = employee?.workshift;

  if (typeof ws === "string") return ws;
  if (ws && typeof ws === "object") return ws.name_workshift || "";
  return "";
};

const getShiftId = (employee) => {
  const ws = employee?.workshift;
  return ws?.id_workshift ?? employee?.workshift_id ?? null;
};

const isSAEmployee = (employee) => {
  const shiftId = Number(getShiftId(employee));
  const shiftName = String(getShiftName(employee) || "");

  return (
    !employee?.description_license &&
    !employee?.work_public_holiday &&
    !employee?.free_day &&
    (shiftId === 1 || /^(S\/A|Sin turno|Sin asignar)$/i.test(shiftName))
  );
};

const hasRealAssignedShift = (employee) => {
  const shiftName = String(getShiftName(employee) || "").trim();

  return (
    !employee?.description_license &&
    !employee?.work_public_holiday &&
    !employee?.free_day &&
    !isSAEmployee(employee) &&
    Boolean(shiftName)
  );
};

const isBeforeEmploymentStart = (day, firstDay) => {
  if (!day || !firstDay) return false;
  return String(day) < String(firstDay);
};

const CalendarTable = ({
  calendarData,
  allCalendarsForPeriod = [], // ✅ NUEVO
  stationWorkshifts = [],
  stationQuantitiesMatrix = [],
  auxAreasByCI = {},
  primaryAreaByCI = {},
  readOnly = false,
  onBusyChange,
  busyState = null,
  combinedWorkareaIds = null, // ids reales de origen cuando la tabla fusiona varias sub-áreas (ver mergeWorkareaGroups)
  onStaffingChanged,
}) => {
  const theme = useTheme();
  const { permissions = [] } = useContext(UserContext) || {};
  const [staffingEdit, setStaffingEdit] = useState(null);

  const station_id = useMemo(
    () => (calendarData?.station_id ? parseInt(calendarData.station_id, 10) : null),
    [calendarData?.station_id]
  );

  const workarea_id = useMemo(
    () => (calendarData?.workarea_id ?? calendarData?.workarea?.id ?? calendarData?.id_workarea ?? null),
    [calendarData?.workarea_id, calendarData?.workarea?.id, calendarData?.id_workarea]
  );

  const rowOrder = useMemo(() => {
    // El row_order que llega directo en calendarData ya es el correcto para
    // esta tabla (incluido el caso fusionado de mergeWorkareaGroups), así que
    // tiene prioridad. El lookup por calendarData.workareas queda como
    // fallback para cuando el caller no lo manda explícito.
    if (Array.isArray(calendarData?.row_order)) {
      return calendarData.row_order.map(String);
    }

    if (!calendarData?.workareas || !workarea_id) {
      return [];
    }

    const currentWorkarea = calendarData.workareas.find(
      wa => String(wa.workarea_id) === String(workarea_id)
    );

    if (currentWorkarea?.row_order && Array.isArray(currentWorkarea.row_order)) {
      return currentWorkarea.row_order.map(String);
    }

    return [];
  }, [calendarData?.workareas, calendarData?.row_order, workarea_id]);

  const rowPos = useMemo(() => {
    const m = {};
    rowOrder.forEach((ci, i) => { m[String(ci)] = i; });
    return m;
  }, [rowOrder]);

  const currentAreaName = calendarData?.workarea_name ?? "Área";

  // Nombres reales de las sub-áreas que componen esta tabla (una sola cuando
  // no está fusionada). El badge de multirol Principal/Secundario compara
  // contra el nombre real de área (viene de employees_meta), no contra el
  // título combinado "Caja + Reponedor" que se muestra arriba de la tabla.
  const currentAreaNames = useMemo(() => {
    const ids = Array.isArray(combinedWorkareaIds) && combinedWorkareaIds.length
      ? combinedWorkareaIds
      : (workarea_id != null ? [workarea_id] : []);
    const namesById = {};
    (calendarData?.workareas || []).forEach((wa) => {
      namesById[String(wa.workarea_id)] = wa.workarea_name;
    });
    return ids
      .map((id) => namesById[String(id)])
      .filter(Boolean);
  }, [calendarData?.workareas, combinedWorkareaIds, workarea_id]);

  const workshifts = useMemo(
    () => (Array.isArray(stationWorkshifts) ? stationWorkshifts : []),
    [stationWorkshifts]
  );

  const enabledWorkshifts = useMemo(
    () => workshifts.filter((w) => w?.enabled),
    [workshifts]
  );

  // La dotación de un área fusionada se guarda en su posición primaria (la de menor id),
  // igual que en Gestión de dotaciones.
  const staffingWorkareaId = useMemo(() => {
    const ids = (Array.isArray(combinedWorkareaIds) && combinedWorkareaIds.length
      ? combinedWorkareaIds
      : (workarea_id != null ? [workarea_id] : [])
    ).map(Number).filter(Number.isFinite);
    return ids.length ? Math.min(...ids) : null;
  }, [combinedWorkareaIds, workarea_id]);

  const canEditStaffing = !readOnly
    && station_id != null
    && staffingWorkareaId != null
    && hasAnyRequiredPermission(permissions, STAFFING_WRITE_PERMISSIONS);

  const principalIds = useMemo(
    () => new Set(workshifts.filter((w) => w?.is_principal).map((w) => String(w.id))),
    [workshifts]
  );

  const expectedById = useMemo(() => {
    if (!Array.isArray(stationQuantitiesMatrix)) {
      return {};
    }

    // Cuando la tabla fusiona varias sub-áreas (ej. Caja + Reponedor bajo
    // MINI), la cobertura esperada por turno es la SUMA de cada sub-área
    // -- no reemplaza sus mínimos configurados por separado, solo los junta
    // para mostrar un único indicador combinado.
    const matchIds = Array.isArray(combinedWorkareaIds) && combinedWorkareaIds.length
      ? combinedWorkareaIds.map(Number)
      : (workarea_id ? [Number(workarea_id)] : []);

    if (!matchIds.length) return {};

    const map = {};
    for (const row of stationQuantitiesMatrix) {
      if (matchIds.includes(Number(row?.workarea_id))) {
        const key = String(row.workshift_id);
        map[key] = (map[key] || 0) + (Number(row.total) || 0);
      }
    }
    return map;
  }, [stationQuantitiesMatrix, workarea_id, combinedWorkareaIds]);

  // ✅ NUEVO: índice multirol usando TODOS los calendarios del período
  const otherRoleShiftIndex = useMemo(() => {
    const index = {};

    const addEntry = (ci, day, value) => {
      const ciKey = String(ci || "").trim();
      if (!ciKey || !day) return;

      if (!index[ciKey]) index[ciKey] = {};
      if (!index[ciKey][day]) index[ciKey][day] = [];
      index[ciKey][day].push(value);
    };

    (allCalendarsForPeriod || []).forEach((calendarWrapper) => {
      const sourceCalendar = calendarWrapper?.calendar_data;
      const sourcePositionId = calendarWrapper?.position_id ?? sourceCalendar?.position_id ?? sourceCalendar?.position;

      const workareas = Array.isArray(sourceCalendar?.workareas) ? sourceCalendar.workareas : [];

      workareas.forEach((wa) => {
        const waId = wa?.workarea_id;
        const waDays = wa?.days || {};

        Object.entries(waDays).forEach(([day, dayData]) => {
          const employees = Array.isArray(dayData?.employees) ? dayData.employees : [];

          employees.forEach((emp) => {
            const ciKey = String(emp?.ci || "").trim();
            if (!ciKey) return;
            if (!hasRealAssignedShift(emp)) return;

            addEntry(ciKey, day, {
              position_id: sourcePositionId,
              workarea_id: waId,
              shift_name: getShiftName(emp),
            });
          });
        });
      });
    });

    return index;
  }, [allCalendarsForPeriod]);

  const multiRoleOverviewIndex = useMemo(() => {
    const index = {};

    const addItem = (ci, day, item) => {
      const ciKey = String(ci || "").trim();
      if (!ciKey || !day) return;

      if (!index[ciKey]) index[ciKey] = {};
      if (!index[ciKey][day]) index[ciKey][day] = [];
      index[ciKey][day].push(item);
    };

    (allCalendarsForPeriod || []).forEach((calendarWrapper) => {
      const sourceCalendar = calendarWrapper?.calendar_data;
      const workareas = Array.isArray(sourceCalendar?.workareas) ? sourceCalendar.workareas : [];

      workareas.forEach((wa) => {
        const waDays = wa?.days || {};

        Object.entries(waDays).forEach(([day, dayData]) => {
          const employees = Array.isArray(dayData?.employees) ? dayData.employees : [];

          employees.forEach((emp) => {
            const workshift = emp?.workshift || {};

            addItem(emp?.ci, day, {
              workarea_id: wa?.workarea_id,
              workarea_name: wa?.workarea_name || "Área",
              role: emp?.work ? "principal" : "auxiliar",
              work: Boolean(emp?.work),
              free_day: Boolean(emp?.free_day),
              license: Boolean(emp?.description_license),
              public_holiday: Boolean(emp?.work_public_holiday),
              workshift: {
                id: workshift?.id_workshift ?? emp?.workshift_id ?? null,
                name: workshift?.name_workshift || "S/A",
                desc: workshift?.description_workshift || "No asignado, turno estándar",
              },
            });
          });
        });
      });
    });

    return index;
  }, [allCalendarsForPeriod]);

  const getOtherRoleShiftName = (ci, day, currentWorkareaId, currentPositionId) => {
    const entries = otherRoleShiftIndex?.[String(ci)]?.[day] || [];

    const otherEntry = entries.find((entry) => {
      const sameWorkarea = String(entry.workarea_id) === String(currentWorkareaId);
      const samePosition = String(entry.position_id) === String(currentPositionId);
      return !(sameWorkarea && samePosition);
    });

    return otherEntry?.shift_name || null;
  };

  const getMultiRoleOverviewItems = (ci, day) =>
    multiRoleOverviewIndex?.[String(ci)]?.[day] || [];

  const days = useMemo(
    () => (calendarData?.days ? Object.keys(calendarData.days) : []),
    [calendarData?.days]
  );

  const employeeFactsByCI = useMemo(() => {
    const facts = {};

    const mergeFact = (emp, day) => {
      const ciKey = String(emp?.ci || "").trim();
      if (!ciKey) return;

      if (!facts[ciKey]) {
        facts[ciKey] = {
          first_day: null,
          first_seen_day: null,
        };
      }

      const firstDay = emp?.first_day ? String(emp.first_day) : null;
      if (firstDay && (!facts[ciKey].first_day || firstDay < facts[ciKey].first_day)) {
        facts[ciKey].first_day = firstDay;
      }

      if (day && (!facts[ciKey].first_seen_day || String(day) < facts[ciKey].first_seen_day)) {
        facts[ciKey].first_seen_day = String(day);
      }
    };

    for (const dataDia of Object.values(calendarData?.days || {})) {
      for (const emp of dataDia?.employees || []) {
        mergeFact(emp, null);
      }
    }

    for (const calendarWrapper of allCalendarsForPeriod || []) {
      const sourceCalendar = calendarWrapper?.calendar_data;
      const workareas = Array.isArray(sourceCalendar?.workareas) ? sourceCalendar.workareas : [];

      workareas.forEach((wa) => {
        Object.entries(wa?.days || {}).forEach(([day, dayData]) => {
          (dayData?.employees || []).forEach((emp) => mergeFact(emp, day));
        });
      });
    }

    return facts;
  }, [allCalendarsForPeriod, calendarData?.days]);

  const empleadosOrdenados = useMemo(() => {
    if (!calendarData?.days) return [];

    const acc = {};
    for (const dataDia of Object.values(calendarData.days)) {
      const empleados = Array.isArray(dataDia?.employees) ? dataDia.employees : [];
      for (const emp of empleados) {
        const ciKey = String(emp?.ci || "").trim();
        if (!ciKey) continue;
        if (!acc[ciKey]) {
          acc[ciKey] = emp;
          continue;
        }

        if (!acc[ciKey]?.first_day && emp?.first_day) {
          acc[ciKey] = { ...acc[ciKey], first_day: emp.first_day };
        }
      }
    }

    const allEmployees = Object.values(acc).map((employee) => {
      const ciKey = String(employee?.ci || "").trim();
      const facts = employeeFactsByCI[ciKey] || {};
      if (employee?.first_day || !facts.first_day) return employee;
      return { ...employee, first_day: facts.first_day };
    });
    const pos = rowPos;

    return allEmployees.sort((a, b) => {
      const ia = pos[String(a.ci)];
      const ib = pos[String(b.ci)];
      const aListed = ia !== undefined;
      const bListed = ib !== undefined;

      if (aListed !== bListed) return aListed ? -1 : 1;
      if (ia !== ib) return (ia ?? 1e9) - (ib ?? 1e9);

      return String(a.surname || "").localeCompare(String(b.surname || "")) ||
        String(a.name || "").localeCompare(String(b.name || "")) ||
        String(a.ci).localeCompare(String(b.ci));
    });
  }, [calendarData?.days, employeeFactsByCI, rowPos]);

  if (!calendarData || !calendarData.days) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body1">Cargando...</Typography>
      </Box>
    );
  }

  const formatDay = (day) => {
    try {
      return format(parseISO(day), "eee d", { locale: es });
    } catch {
      return "Fecha inválida";
    }
  };

  const isWeekendDay = (day) => {
    try {
      const parsed = parseISO(day);
      const dayIndex = getDay(parsed);
      return dayIndex === 0 || dayIndex === 6;
    } catch {
      return false;
    }
  };

  const isHolidayDay = (day) =>
    Boolean(
      (calendarData?.days?.[day]?.employees || []).some((employee) => employee?.is_public_holiday)
    );

  return (
    <TableContainer component={Paper} sx={{ margin: "0 auto", maxWidth: "95vw", overflow: "auto" }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell sx={{ maxWidth: 340, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              Empleado
            </TableCell>
            {days.map((day) => {
              const weekend = isWeekendDay(day);
              const holiday = isHolidayDay(day);
              return (
                <TableCell
                  key={day}
                  align="center"
                  sx={{
                    backgroundColor: holiday ? "#FDEEEE" : weekend ? "#FFF2CC" : "transparent",
                    borderBottom: holiday
                      ? "3px solid #E9B4B4"
                      : weekend
                        ? "3px solid #DDBB63"
                        : undefined,
                    borderLeft: holiday
                      ? "1px solid #F0C9C9"
                      : weekend
                        ? "1px solid #EAD9A5"
                        : undefined,
                    borderRight: holiday
                      ? "1px solid #F6DDDD"
                      : weekend
                        ? "1px solid #F3E8C4"
                        : undefined,
                    color: holiday ? "#8E2A2A" : "inherit",
                    fontWeight: holiday || weekend ? 600 : 400,
                  }}
                >
                  {formatDay(day)}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {empleadosOrdenados.map((employee, index) => {
            const displayIndex = index + 1;
            const primaryArea = primaryAreaByCI?.[employee.ci] ?? null;
            const auxAreas = Array.isArray(auxAreasByCI?.[employee.ci]) ? auxAreasByCI[employee.ci] : [];
            const isBusyEmployee = busyState?.employeeCi === String(employee.ci);
            const showEmployeeSpinner = isBusyEmployee && busyState?.scope === "period";

            const isPrincipalHere = !!primaryArea && currentAreaNames.includes(primaryArea);
            const isAuxHere = auxAreas.some((a) => currentAreaNames.includes(a));
            const hasOtherRoles = auxAreas.length > 0;
            // Sub-área real (ej. Caja/Reponedor) cuando la tabla fusiona varias --
            // se muestra una sola vez junto al nombre, no repetida en cada celda.
            const areaTagAbbrev = employee?.area_tag
              ? employee.area_tag.split('+').map((part) => part.trim().charAt(0).toUpperCase()).join('+')
              : null;

            return (
              <TableRow key={employee.ci}>
                <TableCell>{displayIndex}</TableCell>

                <TableCell
                  sx={{
                    maxWidth: 340,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    backgroundColor: isBusyEmployee ? "rgba(0, 232, 117, 0.08)" : undefined,
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <Tooltip
                    placement="top"
                    title={
                      (() => {
                        const fallbackPrimary =
                          primaryArea ??
                          employee.area_tag ??
                          employee.fixed_primary_area ??
                          employee.primary_area_name ??
                          employee.workarea_name ??
                          employee.name_area ??
                          currentAreaName;

                        const secondaryList = (
                          (Array.isArray(auxAreas) && auxAreas.length ? auxAreas :
                            Array.isArray(employee.fixed_aux_areas) ? employee.fixed_aux_areas : [])
                        )
                          .filter(Boolean)
                          .filter(a => a !== fallbackPrimary);

                        return (
                          <div>
                            <div><strong>{employee.name} {employee.surname}</strong></div>
                            <div><strong>CI:</strong> {employee.ci}</div>
                            <div><strong>Área principal:</strong> {fallbackPrimary || "—"}</div>
                            <div><strong>Áreas secundarias:</strong> {secondaryList.length ? secondaryList.join(", ") : "No tiene"}</div>
                            <div><strong>Fecha de ingreso:</strong> {employee.first_day || employeeFactsByCI[String(employee.ci)]?.first_day || employeeFactsByCI[String(employee.ci)]?.first_seen_day || "—"}</div>
                            <div><strong>Turno base:</strong> {employee.workshift?.name_workshift || "S/A"}</div>
                            <div><strong>Horario:</strong> {employee.workshift?.description_workshift
                              || (employee.workshift?.hour_from && employee.workshift?.hour_to
                                ? `${employee.workshift.hour_from.slice(0, 5)} a ${employee.workshift.hour_to.slice(0, 5)}`
                                : "No asignado, turno estándar")}</div>
                            {employee.is_night && <div><strong>Trabaja en rotación nocturna</strong></div>}
                            {!employee.is_night && employee.override_night && <div><strong>Puede cubrir turno nocturno</strong></div>}
                            {employee.is_manager && <div><strong>Encargado</strong></div>}
                            {!employee.is_manager && employee.override_manager && (
                              <div><strong>Puede cubrir al encargado por asignación manual</strong></div>
                            )}
                          </div>
                        );
                      })()
                    }
                  >
                    <span>
                      {showEmployeeSpinner && (
                        <Box
                          component="span"
                          sx={{
                            mr: 1,
                            display: "inline-flex",
                            verticalAlign: "middle",
                            color: "secondary.main",
                          }}
                        >
                          <CircularProgress size={16} thickness={5} color="inherit" />
                        </Box>
                      )}
                      {employee.is_night && <span style={{ fontWeight: "bold" }}>N </span>}
                      {!employee.is_night && employee.override_night && <span style={{ fontWeight: "bold" }}>CN </span>}
                      {employee.is_manager && (
                        <span style={{ fontWeight: "bold", color: theme.palette.secondary.light }}>E </span>
                      )}
                      {!employee.is_manager && employee.override_manager && (
                        <span style={{ fontWeight: "bold", color: theme.palette.secondary.light }}>CE </span>
                      )}
                      {`${employee.name} ${employee.surname}`}
                      {areaTagAbbrev && (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          ({areaTagAbbrev})
                        </span>
                      )}

                      {hasOtherRoles && (isPrincipalHere || isAuxHere) && (
                        <Box
                          component="span"
                          sx={{ ml: 0.75, display: "inline-flex", verticalAlign: "middle", pointerEvents: "none" }}
                          aria-label={isPrincipalHere ? "Multirol: Principal aquí" : "Multirol: Auxiliar aquí"}
                        >
                          {isPrincipalHere ? (
                            <PeopleAltIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />
                          ) : (
                            <PeopleAltOutlinedIcon fontSize="medium" sx={{ color: theme.palette.primary.main, opacity: 0.95 }} />
                          )}
                        </Box>
                      )}
                    </span>
                  </Tooltip>
                </TableCell>

                {days.map((day) => {
                  const dayEntry =
                    (calendarData.days[day]?.employees || []).find(
                      (e) => String(e.ci) === String(employee.ci)
                    ) || {};

                  const isLib = dayEntry?.free_day;
                  const otherRoleShiftName = getOtherRoleShiftName(
                    employee.ci,
                    day,
                    dayEntry?.workarea_id ?? workarea_id,
                    calendarData?.position_id ?? calendarData?.position
                  );
                  const multiRoleOverviewItems = getMultiRoleOverviewItems(employee.ci, day);
                  const employeeFacts = employeeFactsByCI[String(employee.ci)] || {};
                  const employeeFirstDay =
                    dayEntry?.first_day ||
                    employee.first_day ||
                    employeeFacts.first_day ||
                    employeeFacts.first_seen_day ||
                    null;
                  const employmentLocked = Boolean(
                    dayEntry?.employment_locked || isBeforeEmploymentStart(day, employeeFirstDay)
                  );

                  return (
                    <CalendarCell
                      key={`${employee.ci}-${day}`}
                      day={day}
                      isWeekend={isWeekendDay(day)}
                      isHoliday={isHolidayDay(day)}
                      station_id={station_id}
                      workarea_id={dayEntry?.workarea_id ?? workarea_id}
                      workshifts={enabledWorkshifts}
                      readOnly={readOnly}
                      employeeData={{
                        ...dayEntry,
                        ci: employee.ci,
                        name: employee.name,
                        surname: employee.surname,
                        is_night: Boolean(employee.is_night),
                        override_night: Boolean(employee.override_night),
                        work: Boolean(dayEntry?.work),
                        workshift: dayEntry?.workshift || "Sin turno",
                        workshift_id: dayEntry?.workshift?.id_workshift ?? dayEntry?.workshift_id,
                        workshift_description: dayEntry?.workshift?.description_workshift,
                        workshift_manager: dayEntry?.workshift_manager || false,
                        fixed_primary_area: primaryArea || null,
                        fixed_aux_areas: auxAreas.filter(a => a && a !== primaryArea),
                        otherRoleShiftName,
                        multiRoleOverviewItems,
                        first_day: employeeFirstDay,
                        employment_locked: employmentLocked,
                      }}
                      textStyle={{
                        color: isLib
                          ? "inherit"
                          : dayEntry?.workshift_manager
                            ? theme.palette.secondary.light
                            : "inherit",
                        fontWeight: isLib ? "normal" : dayEntry?.workshift_manager ? "bold" : "normal",
                      }}
                      onBusyChange={onBusyChange}
                      busyState={busyState}
                    />
                  );
                })}
              </TableRow>
            );
          })}

          {workshifts.length > 0 && (
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "center" }} />
              {days.map((day) => {
                const counts = calendarData.days[day]?.workshift_counts || {};
                const turnoElements = enabledWorkshifts
                  .sort((a, b) => a.id - b.id)
                  .map((turno) => {
                    const id = String(turno.id);
                    const count = counts[id];
                    const actual = count?.actual_count ?? 0;
                    const expected = (count?.expected ?? count?.expected_count ?? expectedById[id] ?? 0);
                    if (actual === 0 && expected === 0 && !principalIds.has(id)) return null;

                    let color = "text.secondary";
                    if (expected > 0) {
                      if (actual < expected) color = "error.main";
                      else if (actual > expected) color = "warning.main";
                      else color = "success.main";
                    }

                    const editable = canEditStaffing && id !== "1" && (principalIds.has(id) || expected > 0);
                    const openStaffingEdit = (event) => setStaffingEdit({
                      anchorEl: event.currentTarget, day, turno, actual, expected,
                    });

                    return (
                      <Box
                        key={id}
                        component="div"
                        role={editable ? "button" : undefined}
                        tabIndex={editable ? 0 : undefined}
                        title={editable ? "Editar la dotación de este día" : undefined}
                        onClick={editable ? openStaffingEdit : undefined}
                        onKeyDown={editable ? (e) => { if (e.key === "Enter") openStaffingEdit(e); } : undefined}
                        sx={{
                          color,
                          fontSize: "0.7rem",
                          lineHeight: 1.2,
                          ...(editable ? { cursor: "pointer", "&:hover": { textDecoration: "underline" } } : {}),
                        }}
                      >
                        {`${turno.name}: ${actual}/${expected}`}
                      </Box>
                    );
                  })
                  .filter(Boolean);

                return (
                  <TableCell key={`totals-${day}`} align="center" sx={{ p: "4px" }}>
                    {turnoElements.length ? turnoElements : "-"}
                  </TableCell>
                );
              })}
            </TableRow>
          )}

          {workshifts.length > 0 && (
            <TableRow>
              <TableCell colSpan={2} sx={{ fontWeight: "bold", fontSize: "0.85rem", textAlign: "right" }}>
                Totales
              </TableCell>
              {days.map((day) => {
                const counts = calendarData.days[day]?.workshift_counts || {};
                let actualSum = 0;
                let expectedSum = 0;

                enabledWorkshifts.forEach((w) => {
                  const id = String(w.id);
                  if (id === "1") return;

                  const c = counts[id];
                  const actual = c?.actual_count ?? 0;
                  const expected = (c?.expected ?? c?.expected_count ?? expectedById[id] ?? 0);

                  if (expected > 0 || actual > 0 || principalIds.has(id)) {
                    actualSum += actual;
                    expectedSum += expected;
                  }
                });

                let color = "text.secondary";
                if (expectedSum > 0) {
                  if (actualSum < expectedSum) color = "error.main";
                  else if (actualSum > expectedSum) color = "warning.main";
                  else color = "success.main";
                }

                return (
                  <TableCell key={`summary-${day}`} align="center" sx={{ p: "4px", fontSize: "0.75rem", color }}>
                    {`${actualSum}/${expectedSum}`}
                  </TableCell>
                );
              })}
            </TableRow>
          )}
        </TableBody>
      </Table>

      <StaffingQuickEdit
        edit={staffingEdit}
        stationId={station_id}
        workareaId={staffingWorkareaId}
        onClose={() => setStaffingEdit(null)}
        onSaved={onStaffingChanged}
      />
    </TableContainer>
  );
};

export default CalendarTable;
